import React, { useEffect } from 'react';
import { Box, Tooltip, Typography } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { useWatch } from 'react-hook-form';
import { TrainerSearchSelect } from './TrainerSearchSelect';

// 담당자 순서대로 돌아가며 쓰는 막대 색
const PALETTE = ['#4AB3BC', '#F2A54A', '#8E7CF0', '#5CB85C', '#E06C9F', '#7A9AB8'];

export const FULL_SHARE = 100;

/** 비율 맵의 예약 키. { pending: true } 면 "나중에 분배" — 비율 없이 저장(서버 SHARE_RATE NULL). */
export const PENDING_KEY = 'pending';
export const isSharePending = shares => shares?.[PENDING_KEY] === true;
export const PENDING_SHARES = { [PENDING_KEY]: true };

/** N명 균등 분배. 나머지는 첫 사람에게. (2명 50/50, 3명 34/33/33) */
export const equalShares = ids => {
  const n = ids.length;
  if (n === 0) return {};
  const base = Math.floor(FULL_SHARE / n);
  return ids.reduce((acc, id, i) => ({ ...acc, [id]: i === 0 ? FULL_SHARE - base * (n - 1) : base }), {});
};

export const sellerShareSum = (ids, shares) => ids.reduce((sum, id) => sum + (Number(shares?.[id]) || 0), 0);

/** 1명이면 항상 유효(100 고정). 나중에 분배면 비율 없이 유효. N명이면 각자 1 이상, 합 100. */
export const isSellerShareValid = (ids, shares) =>
  ids.length < 2 ||
  isSharePending(shares) ||
  (ids.every(id => Number(shares?.[id]) >= 1) && sellerShareSum(ids, shares) === FULL_SHARE);

/**
 * 판매 담당자 배열 + 비율 맵 → 서버 요청 필드.
 * registeredBy 는 첫 담당자(구 서버 호환 REGISTERED_BY), registeredByIds/shareRates 는 같은 순서.
 * 나중에 분배면 shareRates 를 비워 보낸다(서버가 N명+비율 없음 = 미분배로 해석). CSV 경로는 join 하면 '' 가 된다.
 */
export const buildSellerPayload = (ids, shares) => {
  const list = Array.isArray(ids) ? ids : [];
  if (!list.length) return { registeredBy: null };
  const rates =
    list.length < 2 ? [FULL_SHARE] : isSharePending(shares) ? [] : list.map(id => Number(shares?.[id]) || 0);
  return { registeredBy: list[0], registeredByIds: list, shareRates: rates };
};

/** 장바구니·확인 화면용 표시 문자열. 1명이면 이름만, N명이면 "이름 50% · 이름 50%", 미분배면 "이름 · 이름 · 나중에 분배". */
export const sellerDisplayName = (ids, shares, options) => {
  const list = Array.isArray(ids) ? ids : [];
  const pending = list.length >= 2 && isSharePending(shares);
  const names = list
    .map(id => {
      const label = options?.find(o => o.value === id)?.label || '';
      return list.length < 2 || pending ? label : `${label} ${Number(shares?.[id]) || 0}%`;
    })
    .filter(Boolean);
  return (pending ? [...names, '나중에 분배'] : names).join(' · ');
};

/**
 * 판매 담당자 검색형 다중 선택 + 귀속 비율.
 * 값 계약 — name: 담당자 user ID 배열(선택 순서 보존), sharesName: { [userId]: 비율 } 맵.
 * 2명 이상이면 드롭다운이 열려 있는 동안 패널 옆에 비율 편집 상자가 같이 뜬다(닫히면 아무것도 안 남긴다 —
 * 사용자 결정 2026-09-15). 한 명이면 비율을 비운다(서버가 100 고정).
 */
export const SellerShareSelect = ({
  control,
  setValue,
  name = 'branchManager',
  sharesName = 'sellerShares',
  options = [],
  placeholder = '담당자 선택',
  onChange,
  disabled = false,
}) => {
  const selectedRaw = useWatch({ control, name });
  const selected = Array.isArray(selectedRaw) ? selectedRaw : [];
  const shares = useWatch({ control, name: sharesName }) || {};
  const selectedKey = selected.join(',');

  // 선택 집합이 바뀌면 비율을 균등 분배로 다시 채운다(그 뒤 사용자가 고친다)
  useEffect(() => {
    // 1명만 골라도 필수 오류 표시를 지워야 하므로 선택이 바뀔 때마다 알린다
    onChange?.();
    if (selected.length < 2) {
      if (Object.keys(shares).length) setValue(sharesName, {});
      return;
    }
    // 나중에 분배(토글 OFF)면 비율 맵을 건드리지 않는다
    if (isSharePending(shares)) return;
    const aligned = Object.keys(shares).length === selected.length && selected.every(id => shares[id] != null);
    if (!aligned) setValue(sharesName, equalShares(selected));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedKey]);

  const labelOf = id => options.find(o => o.value === id)?.label || String(id);
  const pending = isSharePending(shares);
  const sum = sellerShareSum(selected, shares);
  const valid = isSellerShareValid(selected, shares);
  // 분배 토글 — OFF 면 비율 없이 저장(나중에 판매정보 수정·급여정산에서 분배)
  const setPending = nextPending => {
    setValue(sharesName, nextPending ? PENDING_SHARES : equalShares(selected));
    onChange?.();
  };
  // 담당자 제외 — 남은 사람들은 선택 집합 변경 효과로 균등 분배된다
  const removeSeller = id => {
    setValue(
      name,
      selected.filter(selectedId => selectedId !== id),
    );
  };

  const setShare = (id, raw) => {
    const digits = String(raw).replace(/\D/g, '').slice(0, 3);
    setValue(sharesName, { ...shares, [id]: digits === '' ? '' : Number(digits) });
    onChange?.();
  };

  const bar = height => (
    <Box
      sx={{ display: 'flex', height, borderRadius: '999px', overflow: 'hidden', backgroundColor: '#e0e0e0', flex: 1 }}>
      {selected.map((id, i) => (
        <Box
          key={id}
          sx={{
            width: `${Math.max(0, Math.min(FULL_SHARE, Number(shares[id]) || 0))}%`,
            backgroundColor: PALETTE[i % PALETTE.length],
            transition: 'width 0.15s ease',
          }}
        />
      ))}
    </Box>
  );

  // 비율 편집 — 담당자 드롭다운이 열려 있는 동안 그 옆에 뜬다
  const rateEditor = (
    <Box sx={{ p: '12px 14px' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: '8px' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
          <Typography sx={{ fontSize: '13px', fontWeight: 700, color: '#222' }}>매출 귀속 비율</Typography>
          <Tooltip
            arrow
            placement="top"
            title="급여정산 개인·직급 성과급에서 담당자별 실적을 나누는 비율입니다. 분배 OFF 로 저장하면 판매정보 수정에서 나중에 정할 수 있습니다.">
            <InfoOutlinedIcon sx={{ fontSize: 15, color: '#999', cursor: 'help' }} />
          </Tooltip>
        </Box>
        {/* 분배 ON/OFF — OFF 면 비율 없이 저장하고 나중에 정한다 */}
        <Box
          onClick={() => setPending(!pending)}
          title={pending ? '비율을 나중에 정합니다' : '비율을 지금 정합니다'}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '5px',
            cursor: 'pointer',
            userSelect: 'none',
          }}>
          <Box
            sx={{
              width: '30px',
              height: '16px',
              borderRadius: '999px',
              backgroundColor: pending ? '#c4c4c4' : '#4AB3BC',
              position: 'relative',
              transition: 'background-color 0.15s ease',
            }}>
            <Box
              sx={{
                position: 'absolute',
                top: '2px',
                left: pending ? '2px' : '16px',
                width: '12px',
                height: '12px',
                borderRadius: '50%',
                backgroundColor: '#fff',
                transition: 'left 0.15s ease',
              }}
            />
          </Box>
          <Typography sx={{ fontSize: '12px', fontWeight: 600, color: pending ? '#737373' : '#4AB3BC' }}>
            {pending ? '분배 OFF' : '분배 ON'}
          </Typography>
        </Box>
      </Box>
      {!pending && <Box sx={{ mb: '8px', display: 'flex' }}>{bar('8px')}</Box>}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
        {selected.map((id, i) => (
          <Box
            key={id}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              position: 'relative',
              // 이름 영역에 마우스를 올리면(퍼센트 입력 제외) 행 정중앙으로 휴지통이 스르륵 나타난다
              '& .seller-row-label:hover ~ .seller-row-remove, & .seller-row-remove:hover': {
                opacity: 1,
                transform: 'translate(-50%, -50%) translateX(0)',
              },
              '& .seller-row-label:hover .seller-row-name': { opacity: 0.35 },
            }}>
            <Box
              className="seller-row-label"
              sx={{
                flex: 1,
                minWidth: 0,
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                height: '30px',
                cursor: 'default',
              }}>
              <Box
                sx={{
                  width: '8px',
                  height: '8px',
                  borderRadius: '50%',
                  backgroundColor: PALETTE[i % PALETTE.length],
                  flexShrink: 0,
                }}
              />
              <Typography
                className="seller-row-name"
                sx={{
                  flex: 1,
                  fontSize: '13px',
                  color: '#333',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  transition: 'opacity 0.18s ease',
                }}>
                {labelOf(id)}
              </Typography>
            </Box>
            {!pending && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  border: '1px solid #c4c4c4',
                  borderRadius: '6px',
                  padding: '0 6px 0 4px',
                  height: '30px',
                  width: '52px',
                  flexShrink: 0,
                  '&:focus-within': { borderColor: '#4AB3BC' },
                }}>
                <Box
                  component="input"
                  value={shares[id] ?? ''}
                  onChange={e => setShare(id, e.target.value)}
                  onFocus={e => e.target.select()}
                  inputMode="numeric"
                  sx={{
                    width: '100%',
                    minWidth: 0,
                    border: 'none',
                    outline: 'none',
                    background: 'transparent',
                    textAlign: 'center',
                    fontSize: '13px',
                    fontWeight: 600,
                    color: '#333',
                    padding: 0,
                    fontFamily: 'inherit',
                  }}
                />
                <Typography sx={{ fontSize: '12px', color: '#737373', ml: '3px' }}>%</Typography>
              </Box>
            )}
            <Box
              className="seller-row-remove"
              onClick={() => removeSeller(id)}
              title="담당자 제외"
              sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%) translateX(-10px)',
                opacity: 0,
                transition: 'opacity 0.18s ease, transform 0.18s ease',
                width: '26px',
                height: '26px',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                color: '#d32f2f',
                backgroundColor: '#fff',
                boxShadow: '0 1px 4px rgba(0,0,0,0.18)',
                '&:hover': { backgroundColor: '#fdecec' },
              }}>
              <DeleteOutlineIcon sx={{ fontSize: 16 }} />
            </Box>
          </Box>
        ))}
      </Box>
      {!pending && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            mt: '8px',
            pt: '8px',
            borderTop: '1px solid #eee',
          }}>
          {/* 한 줄 텍스트 높이 통일 — 글자 크기가 달라도 같은 line-height 로 정렬 */}
          <Typography
            onClick={() => setValue(sharesName, equalShares(selected))}
            sx={{
              fontSize: '13px',
              lineHeight: '20px',
              fontWeight: 600,
              color: '#4AB3BC',
              cursor: 'pointer',
              '&:hover': { textDecoration: 'underline' },
            }}>
            균등 분배
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Typography sx={{ fontSize: '13px', lineHeight: '20px', color: valid ? '#737373' : '#d32f2f' }}>
              {valid
                ? '합계'
                : sum === FULL_SHARE
                  ? '1% 이상이어야 합니다'
                  : `${Math.abs(FULL_SHARE - sum)}% ${sum > FULL_SHARE ? '초과' : '부족'}`}
            </Typography>
            <Typography
              sx={{ fontSize: '13px', lineHeight: '20px', fontWeight: 700, color: valid ? '#4AB3BC' : '#d32f2f' }}>
              {sum}%
            </Typography>
          </Box>
        </Box>
      )}
    </Box>
  );

  return (
    <Box sx={{ width: '100%' }}>
      <TrainerSearchSelect
        control={control}
        name={name}
        options={options}
        placeholder={placeholder}
        searchPlaceholder="담당자 검색"
        overlay
        disabled={disabled}
        sidePanel={selected.length >= 2 ? rateEditor : null}
      />
    </Box>
  );
};

export default SellerShareSelect;
